import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useRouteMatch, Route } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import {
  Box,
  Grid
} from '@material-ui/core';

import { fetchWheel, selectFetchStatus, selectAccess, resetStatus } from './wheelSlice';
import { fetchItemFamilies, selectItemFamilies } from '../itemFamilies/itemFamiliesSlice';

import TopSection from '../common/topSection';
import BreadCrumb from '../common/breadcrumb';
import Alert from '../common/alert';
import Loading from '../common/loading';
import PinButton from '../pins/PinButton';

import StatusTabs from '../itemFamily/StatusTabs'; // Consider making common?
import WheelPagePC24 from './wheelPagePC24';
import useTitle from '../../hooks/use-title.hook';
import useIsFirstRender from '../../hooks/use-is-first-render.hook';


const ownerSpecificComponents = {
  PC24: WheelPagePC24,
};

const Wheel = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { itemOwnerId, wheelId } = useParams();
  const routeMatch = useRouteMatch();
  const { fetchStatus, fetchError, ...wheel } = useSelector(selectFetchStatus);
  const { status: familyStatus } = useSelector(selectItemFamilies);
  const canEdit = useSelector(selectAccess);
  const firstRender = useIsFirstRender();

  const breadcrumbItems = [
    {name: itemOwnerId, link: `/pih/owner/${itemOwnerId}`},
    {name: t('nav:wheels'), link: `/pih/owner/${itemOwnerId}/wheels`},
    wheel.draft.name ? {name: wheel.draft.name} : {name: wheelId}
  ].filter(Boolean);

  const statusTabs = [
    {id: 0, url: routeMatch.url, label: t('common:draft')},
    {id: 1, url: `${routeMatch.url}/published`, label: t('common:published'), disabled: wheel.published === null}
  ];

  useEffect(() => {
    // Fetch data from server
    dispatch(fetchWheel({ownerId: itemOwnerId, wheelId: wheelId}));

    return () => {
      // Empty state on unmount
      // ckeditor causes error if it is unmounted before it has been initialised (the fraction the old family is visible before useEffect fetches new data)
      dispatch(resetStatus());
    }
  }, [dispatch, wheelId]);

  useEffect(() => {
    // Fetch also item families if not present in redux
    // Needed for linking wheel to item families.
    if(firstRender && familyStatus === 'idle') {
      dispatch(fetchItemFamilies({ownerId: itemOwnerId}));
    }
  }, [dispatch, familyStatus, itemOwnerId]);

  useTitle(wheel?.draft?.name);

  return (
    <>
      <TopSection>
        <Grid container justify="space-between" alignItems="center">
          <Grid item>
            <BreadCrumb items={breadcrumbItems} />
          </Grid>
          <Grid item>
            {wheel.draft.code ? <PinButton itemCode={wheel.draft.code} itemName={wheel.draft.name} itemOwner={itemOwnerId} itemType="wheel" /> : null }
          </Grid>
        </Grid>
      </TopSection>

      <Box p={3.5}>
        <StatusTabs tabs={statusTabs} />
      </Box>

      <Box p={3.5} pb="70px">
        {fetchStatus === 'loading' ? <Loading /> : null}
        {fetchStatus === 'failed' ? <Alert severity="error">{t(`errors:${fetchError}`)}</Alert> : null}
        {fetchStatus === 'succeeded'
          ?
          <Route path={`${routeMatch.path}/:status?`} render={routeProps => {
            const status = routeProps.match.params.status || 'draft'; // default to draft (default is the one without url param)
            const data = wheel[status];
            const Component = ownerSpecificComponents[itemOwnerId];

            if(Component) {
              // Key forces it to re-mount when status changes
              return <Component key={status} status={status} wheel={data} canEdit={canEdit} />;
            } else {
              return <Alert severity="error">View not implemented for owner {itemOwnerId}</Alert>;
            }
          }}
          />
          :
          null
        }
      </Box>
    </>
  );
};

export default Wheel;
